/**
 * PrintingPage - Consolidated admin page for all print-related functionality.
 *
 * Includes:
 * - Print paper buttons (teams, judges)
 * - Print contacts management (päätuomarit, tulospalvelu)
 * - Media assets management (logos, advertisements)
 */

import { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Grid,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import UploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { contactsApi, mediaApi } from '@/api/client';
import type { ContactCreateRequest } from '@/api/client';
import type { Contact, ContactRole, MediaAsset, MediaAssetType } from '@/types';

// ============================================================================
// Constants
// ============================================================================

const ROLE_OPTIONS: { value: ContactRole; label: string; color: 'primary' | 'secondary' | 'success' | 'info' }[] = [
  { value: 'paatuomari-firma', label: 'Päätuomari (Firma)', color: 'primary' },
  { value: 'paatuomari-pro', label: 'Päätuomari (PRO)', color: 'secondary' },
  { value: 'paatuomari-yle', label: 'Päätuomari (Yleinen)', color: 'success' },
  { value: 'tulospalvelu', label: 'Tulospalvelu', color: 'info' },
];

const IMAGE_TYPE_OPTIONS: { value: MediaAssetType; label: string; color: 'primary' | 'secondary' | 'success' | 'info' }[] = [
  { value: 'club-logo', label: 'Club Logo', color: 'primary' },
  { value: 'kyykka-logo', label: 'Kyykkä Logo', color: 'secondary' },
  { value: 'advertisement', label: 'Advertisement', color: 'success' },
  { value: 'sidebar-logo', label: 'Sidebar Logo', color: 'info' },
];

// ============================================================================
// Component
// ============================================================================

export function PrintingPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---------------------------------------------------------------------------
  // Contacts State
  // ---------------------------------------------------------------------------
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formRole, setFormRole] = useState<ContactRole>('paatuomari-yle');
  const [formOrder, setFormOrder] = useState(0);
  const [formActive, setFormActive] = useState(true);
  const [deleteContactTarget, setDeleteContactTarget] = useState<Contact | null>(null);

  // ---------------------------------------------------------------------------
  // Media State
  // ---------------------------------------------------------------------------
  const [mediaTypeFilter, setMediaTypeFilter] = useState<string>('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadType, setUploadType] = useState<MediaAssetType>('advertisement');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [deleteMediaTarget, setDeleteMediaTarget] = useState<MediaAsset | null>(null);

  // ---------------------------------------------------------------------------
  // Contacts Queries & Mutations
  // ---------------------------------------------------------------------------
  const {
    data: contacts = [],
    isLoading: contactsLoading,
    error: contactsError,
  } = useQuery<Contact[]>({
    queryKey: ['contacts'],
    queryFn: () => contactsApi.list(),
  });

  const createContactMutation = useMutation({
    mutationFn: (data: ContactCreateRequest) => contactsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      handleCloseContactDialog();
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ContactCreateRequest> }) =>
      contactsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      handleCloseContactDialog();
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: (id: number) => contactsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setDeleteContactTarget(null);
    },
  });

  // ---------------------------------------------------------------------------
  // Media Queries & Mutations
  // ---------------------------------------------------------------------------
  const {
    data: assets = [],
    isLoading: assetsLoading,
    error: assetsError,
  } = useQuery<MediaAsset[]>({
    queryKey: ['media'],
    queryFn: () => mediaApi.list(),
  });

  const uploadMutation = useMutation({
    mutationFn: (data: { name: string; image_type: MediaAssetType; file: File }) =>
      mediaApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      handleCloseUpload();
    },
  });

  const toggleMediaMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      mediaApi.update(id, { active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
  });

  const deleteMediaMutation = useMutation({
    mutationFn: (id: number) => mediaApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      setDeleteMediaTarget(null);
    },
  });

  // ---------------------------------------------------------------------------
  // Contacts Handlers
  // ---------------------------------------------------------------------------
  const handleAddContact = () => {
    setEditingContact(null);
    setFormName('');
    setFormPhone('');
    setFormRole('paatuomari-yle');
    setFormOrder(0);
    setFormActive(true);
    setContactDialogOpen(true);
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setFormName(contact.name);
    setFormPhone(contact.phone);
    setFormRole(contact.role);
    setFormOrder(contact.order);
    setFormActive(contact.active);
    setContactDialogOpen(true);
  };

  const handleCloseContactDialog = () => {
    setContactDialogOpen(false);
    setEditingContact(null);
    setFormName('');
    setFormPhone('');
  };

  const handleSaveContact = () => {
    if (!formName.trim() || !formPhone.trim()) return;
    const data: ContactCreateRequest = {
      name: formName.trim(),
      phone: formPhone.trim(),
      role: formRole,
      order: formOrder,
      active: formActive,
    };
    if (editingContact) {
      updateContactMutation.mutate({ id: editingContact.id, data });
    } else {
      createContactMutation.mutate(data);
    }
  };

  const handleToggleContactActive = (contact: Contact) => {
    updateContactMutation.mutate({
      id: contact.id,
      data: { active: !contact.active },
    });
  };

  // ---------------------------------------------------------------------------
  // Media Handlers
  // ---------------------------------------------------------------------------
  const filteredAssets = mediaTypeFilter
    ? assets.filter((a) => a.image_type === mediaTypeFilter)
    : assets;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      if (!uploadName) {
        setUploadName(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleUpload = () => {
    if (!uploadFile || !uploadName) return;
    uploadMutation.mutate({
      name: uploadName,
      image_type: uploadType,
      file: uploadFile,
    });
  };

  const handleCloseUpload = () => {
    setUploadOpen(false);
    setUploadName('');
    setUploadType('advertisement');
    setUploadFile(null);
    setUploadPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isContactSaving = createContactMutation.isPending || updateContactMutation.isPending;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Printing
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage print papers, contacts, and media assets for team and judge prints.
      </Typography>

      {/* ===================================================================== */}
      {/* Section 1: Print Papers */}
      {/* ===================================================================== */}
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Print Papers
      </Typography>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Generate printable papers for teams (joukkuelaput) and judges (tuomarilaput). Opens in a new tab for printing.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => window.open('/print/teams', '_blank')}
          >
            Print Team Papers
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => window.open('/print/judges', '_blank')}
          >
            Print Judge Papers
          </Button>
        </Box>
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* ===================================================================== */}
      {/* Section 2: Print Contacts */}
      {/* ===================================================================== */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          Print Contacts
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddContact}>
          Add Contact
        </Button>
      </Box>

      {contactsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load contacts
        </Alert>
      )}

      <Paper sx={{ mb: 4 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="center">Order</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contactsLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">Loading...</TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No contacts yet. Add your first contact to get started.
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => {
                  const roleConfig = ROLE_OPTIONS.find((r) => r.value === contact.role);
                  return (
                    <TableRow key={contact.id} sx={{ opacity: contact.active ? 1 : 0.5 }}>
                      <TableCell>
                        <Typography fontWeight={500}>{contact.name}</Typography>
                      </TableCell>
                      <TableCell>{contact.phone}</TableCell>
                      <TableCell>
                        <Chip
                          label={roleConfig?.label || contact.role}
                          color={roleConfig?.color || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">{contact.order}</TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={contact.active}
                          onChange={() => handleToggleContactActive(contact)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEditContact(contact)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteContactTarget(contact)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* ===================================================================== */}
      {/* Section 3: Media Assets */}
      {/* ===================================================================== */}
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Media Assets
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage logos and advertisements used in team and judge prints.
      </Typography>

      {assetsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load media assets
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={mediaTypeFilter}
              label="Type"
              onChange={(e) => setMediaTypeFilter(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              {IMAGE_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {filteredAssets.length} assets
          </Typography>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadOpen(true)}
          >
            Upload Asset
          </Button>
        </Box>
      </Paper>

      {assetsLoading ? (
        <Typography>Loading...</Typography>
      ) : filteredAssets.length === 0 ? (
        <Alert severity="info">
          No media assets found. Upload your first asset to get started.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredAssets.map((asset) => {
            const typeConfig = IMAGE_TYPE_OPTIONS.find((t) => t.value === asset.image_type);
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={asset.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    component="img"
                    height="140"
                    image={asset.file_url}
                    alt={asset.name}
                    sx={{ objectFit: 'contain', bgcolor: 'grey.100', p: 1 }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {asset.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                      <Chip
                        label={typeConfig?.label || asset.image_type}
                        size="small"
                        color={typeConfig?.color || 'default'}
                        variant="outlined"
                      />
                      {asset.active ? (
                        <Chip icon={<CheckCircleIcon />} label="Active" size="small" color="success" />
                      ) : (
                        <Chip icon={<CancelIcon />} label="Inactive" size="small" color="default" />
                      )}
                    </Box>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={asset.active}
                          onChange={(e) =>
                            toggleMediaMutation.mutate({ id: asset.id, active: e.target.checked })
                          }
                          size="small"
                        />
                      }
                      label="Active"
                    />
                    <IconButton
                      color="error"
                      onClick={() => setDeleteMediaTarget(asset)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ===================================================================== */}
      {/* Contact Add/Edit Dialog */}
      {/* ===================================================================== */}
      <Dialog open={contactDialogOpen} onClose={handleCloseContactDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              fullWidth
              required
              placeholder="e.g., Matti Meikäläinen"
            />
            <TextField
              label="Phone"
              value={formPhone}
              onChange={(e) => setFormPhone(e.target.value)}
              fullWidth
              required
              placeholder="e.g., +358 40 123 4567"
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formRole}
                label="Role"
                onChange={(e) => setFormRole(e.target.value as ContactRole)}
              >
                {ROLE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Order"
              type="number"
              value={formOrder}
              onChange={(e) => setFormOrder(parseInt(e.target.value) || 0)}
              fullWidth
              helperText="Lower numbers appear first within the same role"
            />
            <FormControlLabel
              control={
                <Switch checked={formActive} onChange={(e) => setFormActive(e.target.checked)} />
              }
              label="Active (shown on prints)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContactDialog}>Cancel</Button>
          <Button
            onClick={handleSaveContact}
            variant="contained"
            disabled={!formName.trim() || !formPhone.trim() || isContactSaving}
          >
            {isContactSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact Delete Dialog */}
      <Dialog open={!!deleteContactTarget} onClose={() => setDeleteContactTarget(null)}>
        <DialogTitle>Delete Contact?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteContactTarget?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteContactTarget(null)}>Cancel</Button>
          <Button
            onClick={() => deleteContactTarget && deleteContactMutation.mutate(deleteContactTarget.id)}
            color="error"
            variant="contained"
            disabled={deleteContactMutation.isPending}
          >
            {deleteContactMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================================== */}
      {/* Media Upload Dialog */}
      {/* ===================================================================== */}
      <Dialog open={uploadOpen} onClose={handleCloseUpload} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Media Asset</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={uploadType}
                label="Type"
                onChange={(e) => setUploadType(e.target.value as MediaAssetType)}
              >
                {IMAGE_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />} fullWidth>
                {uploadFile ? uploadFile.name : 'Select Image'}
              </Button>
            </label>
            {uploadPreview && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                <img
                  src={uploadPreview}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                />
              </Box>
            )}
            {uploadMutation.isError && (
              <Alert severity="error">Failed to upload asset. Please try again.</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpload}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!uploadFile || !uploadName || uploadMutation.isPending}
          >
            {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Media Delete Dialog */}
      <Dialog open={!!deleteMediaTarget} onClose={() => setDeleteMediaTarget(null)}>
        <DialogTitle>Delete Asset</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &ldquo;{deleteMediaTarget?.name}&rdquo;?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteMediaTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteMediaTarget && deleteMediaMutation.mutate(deleteMediaTarget.id)}
            disabled={deleteMediaMutation.isPending}
          >
            {deleteMediaMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
